import React, { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface FacadeState {
  wallHeight: string;
  wallWidth: string;
  windowsArea: string;
  doorsArea: string;
  insulationType: 'eps' | 'mineral-wool' | 'xps';
  insulationThickness: string;
  facadeType: 'acrylic' | 'silicone' | 'silicate' | 'mineral';
  includeScaffolding: boolean;
  includeMeshAndGlue: boolean;
  includeCornerProfiles: boolean;
}

interface FacadeResult {
  totalArea: number;
  netArea: number;
  insulationArea: number;
  insulationCost: number;
  facadeMaterialAmount: number;
  facadeMaterialCost: number;
  meshAndGlueCost: number;
  cornerProfilesLength: number;
  cornerProfilesCost: number;
  scaffoldingCost: number;
  laborCost: number;
  totalCost: number;
}

// Material prices per m² or m in EUR
const materialPrices = {
  insulation: {
    'eps': { 
      '5': 8, '8': 12, '10': 15, '12': 18, '15': 22, '20': 30 
    },
    'mineral-wool': { 
      '5': 12, '8': 18, '10': 22, '12': 26, '15': 32, '20': 42 
    },
    'xps': { 
      '5': 14, '8': 20, '10': 25, '12': 30, '15': 38, '20': 50 
    }
  },
  facade: {
    'acrylic': 8,
    'silicone': 12,
    'silicate': 10,
    'mineral': 7
  },
  meshAndGlue: 6, // per m²
  cornerProfiles: 3, // per meter
  scaffolding: 5, // per m²
  labor: 20 // per m²
};

function KalkulatorFasade() {
  const [values, setValues] = useState<FacadeState>({
    wallHeight: '',
    wallWidth: '',
    windowsArea: '',
    doorsArea: '',
    insulationType: 'eps',
    insulationThickness: '10',
    facadeType: 'acrylic',
    includeScaffolding: true,
    includeMeshAndGlue: true,
    includeCornerProfiles: true
  });

  const [results, setResults] = useState<FacadeResult | null>(null);

  const calculateFacade = () => {
    const height = parseFloat(values.wallHeight);
    const width = parseFloat(values.wallWidth);
    const windowsArea = parseFloat(values.windowsArea) || 0;
    const doorsArea = parseFloat(values.doorsArea) || 0;
    
    if (isNaN(height) || isNaN(width)) {
      return null;
    }

    // Calculate total and net wall area
    const totalArea = height * width;
    const netArea = totalArea - windowsArea - doorsArea;
    
    // Calculate insulation area (same as net area)
    const insulationArea = netArea;
    
    // Calculate insulation cost
    const insulationPrice = materialPrices.insulation[values.insulationType][values.insulationThickness];
    const insulationCost = insulationArea * insulationPrice;
    
    // Calculate facade material amount (kg) - typically 3kg per m²
    const facadeMaterialAmount = netArea * 3;
    
    // Calculate facade material cost
    const facadeMaterialCost = netArea * materialPrices.facade[values.facadeType];
    
    // Calculate mesh and glue cost if included
    const meshAndGlueCost = values.includeMeshAndGlue ? netArea * materialPrices.meshAndGlue : 0;
    
    // Calculate corner profiles length and cost if included
    // Estimate: perimeter + 2 * height for each window/door (rough estimate)
    const numOpenings = Math.ceil((windowsArea + doorsArea) / 2); // Rough estimate of number of openings
    const cornerProfilesLength = values.includeCornerProfiles ? 
      (2 * (height + width)) + (numOpenings * 2 * height) : 0;
    const cornerProfilesCost = cornerProfilesLength * materialPrices.cornerProfiles;
    
    // Calculate scaffolding cost if included
    const scaffoldingCost = values.includeScaffolding ? totalArea * materialPrices.scaffolding : 0;
    
    // Calculate labor cost
    const laborCost = netArea * materialPrices.labor;
    
    // Calculate total cost
    const totalCost = insulationCost + facadeMaterialCost + meshAndGlueCost + 
                      cornerProfilesCost + scaffoldingCost + laborCost;
    
    return {
      totalArea,
      netArea,
      insulationArea,
      insulationCost,
      facadeMaterialAmount,
      facadeMaterialCost,
      meshAndGlueCost,
      cornerProfilesLength,
      cornerProfilesCost,
      scaffoldingCost,
      laborCost,
      totalCost
    };
  };

  const handleInputChange = (field: keyof FacadeState, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.wallHeight && values.wallWidth) {
      const results = calculateFacade();
      setResults(results);
    }
  }, [values]);

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const insulationTypeNames = {
    'eps': 'Stiropor (EPS)',
    'mineral-wool': 'Kamena vuna',
    'xps': 'Ekstrudirana polistirenska pjena (XPS)'
  };

  const facadeTypeNames = {
    'acrylic': 'Akrilatna',
    'silicone': 'Silikonska',
    'silicate': 'Silikatna',
    'mineral': 'Mineralna'
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Fasade",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte potrebne materijale i troškove za fasadu. Uključuje izolaciju, završni sloj, mrežicu, ljepilo i procjenu rada.",
    "url": "https://online-kalkulator.com/kalkulator-fasade",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun površine fasade",
      "Izračun potrebnih materijala",
      "Procjena troškova",
      "Različite vrste izolacije",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Fasade | Izračun Materijala i Troškova za Fasadu"
        description="Izračunajte potrebne materijale i troškove za fasadu. Uključuje izolaciju, završni sloj, mrežicu, ljepilo i procjenu rada."
        canonicalUrl="/kalkulator-fasade"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Fasade</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Wall Dimensions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visina zida (m)
                  </label>
                  <input
                    type="number"
                    value={values.wallHeight}
                    onChange={(e) => handleInputChange('wallHeight', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 2.8"
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Širina zida (m)
                  </label>
                  <input
                    type="number"
                    value={values.wallWidth}
                    onChange={(e) => handleInputChange('wallWidth', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 10"
                    step="0.1"
                  />
                </div>
              </div>

              {/* Openings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Površina prozora (m²)
                  </label>
                  <input
                    type="number"
                    value={values.windowsArea}
                    onChange={(e) => handleInputChange('windowsArea', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 4"
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Površina vrata (m²)
                  </label>
                  <input
                    type="number"
                    value={values.doorsArea}
                    onChange={(e) => handleInputChange('doorsArea', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 2"
                    step="0.1"
                  />
                </div>
              </div>

              {/* Insulation Type and Thickness */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vrsta izolacije
                  </label>
                  <select
                    value={values.insulationType}
                    onChange={(e) => handleInputChange('insulationType', e.target.value as FacadeState['insulationType'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="eps">Stiropor (EPS)</option>
                    <option value="mineral-wool">Kamena vuna</option>
                    <option value="xps">Ekstrudirana polistirenska pjena (XPS)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Debljina izolacije (cm)
                  </label>
                  <select
                    value={values.insulationThickness}
                    onChange={(e) => handleInputChange('insulationThickness', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="5">5 cm</option>
                    <option value="8">8 cm</option>
                    <option value="10">10 cm</option>
                    <option value="12">12 cm</option>
                    <option value="15">15 cm</option>
                    <option value="20">20 cm</option>
                  </select>
                </div>
              </div>

              {/* Facade Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vrsta završnog sloja
                </label>
                <select
                  value={values.facadeType}
                  onChange={(e) => handleInputChange('facadeType', e.target.value as FacadeState['facadeType'])}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="acrylic">Akrilatna fasada</option>
                  <option value="silicone">Silikonska fasada</option>
                  <option value="silicate">Silikatna fasada</option>
                  <option value="mineral">Mineralna fasada</option>
                </select>
              </div>

              {/* Additional Options */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeScaffolding}
                    onChange={(e) => handleInputChange('includeScaffolding', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Uključi skelu</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeMeshAndGlue}
                    onChange={(e) => handleInputChange('includeMeshAndGlue', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Uključi mrežicu i ljepilo</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeCornerProfiles}
                    onChange={(e) => handleInputChange('includeCornerProfiles', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Uključi kutne profile</span>
                </label>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ukupna površina</div>
                      <div className="text-xl font-bold text-blue-600">{formatNumber(results.totalArea)} m²</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Neto površina</div>
                      <div className="text-xl font-bold text-blue-600">{formatNumber(results.netArea)} m²</div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium text-gray-700">Materijali i troškovi:</h3>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        {insulationTypeNames[values.insulationType]} ({values.insulationThickness} cm):
                      </span>
                      <span className="font-medium">{formatNumber(results.insulationCost)} €</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        {facadeTypeNames[values.facadeType]} fasada ({formatNumber(results.facadeMaterialAmount)} kg):
                      </span>
                      <span className="font-medium">{formatNumber(results.facadeMaterialCost)} €</span>
                    </div>
                    
                    {values.includeMeshAndGlue && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Mrežica i ljepilo:</span>
                        <span className="font-medium">{formatNumber(results.meshAndGlueCost)} €</span>
                      </div>
                    )}
                    
                    {values.includeCornerProfiles && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">
                          Kutni profili ({formatNumber(results.cornerProfilesLength)} m):
                        </span>
                        <span className="font-medium">{formatNumber(results.cornerProfilesCost)} €</span>
                      </div>
                    )}
                    
                    {values.includeScaffolding && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Skela:</span>
                        <span className="font-medium">{formatNumber(results.scaffoldingCost)} €</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Rad:</span>
                      <span className="font-medium">{formatNumber(results.laborCost)} €</span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-gray-800 font-medium">Ukupni trošak:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatNumber(results.totalCost)} €
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Fasade</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator fasade je koristan alat koji vam pomaže izračunati potrebne količine
                  materijala i procjenu troškova za postavljanje fasade. Uzima u obzir dimenzije
                  zidova, vrstu izolacije, završni sloj i dodatne materijale kako bi vam pružio
                  sveobuhvatnu procjenu.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Vrste Izolacije</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Stiropor (EPS)</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Najčešće korištena i najjeftinija opcija</li>
                      <li>Dobra toplinska izolacija</li>
                      <li>Manja otpornost na vlagu</li>
                      <li>Nije otporan na požar</li>
                      <li>Koeficijent toplinske vodljivosti: 0.035-0.040 W/mK</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Kamena vuna</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Odlična toplinska i zvučna izolacija</li>
                      <li>Paropropusna - "diše"</li>
                      <li>Otporna na požar</li>
                      <li>Viša cijena od stiropora</li>
                      <li>Koeficijent toplinske vodljivosti: 0.035-0.040 W/mK</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">XPS (Ekstrudirana polistirenska pjena)</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Visoka otpornost na vlagu</li>
                      <li>Visoka mehanička čvrstoća</li>
                      <li>Idealna za sokl i podzemne dijelove</li>
                      <li>Najviša cijena</li>
                      <li>Koeficijent toplinske vodljivosti: 0.030-0.035 W/mK</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Vrste Završnih Slojeva</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Akrilatna fasada</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Dobra otpornost na vremenske uvjete</li>
                      <li>Širok izbor boja</li>
                      <li>Srednja paropropusnost</li>
                      <li>Dobra cijena</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Silikonska fasada</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Visoka otpornost na prljavštinu i vodu</li>
                      <li>Odlična paropropusnost</li>
                      <li>Dugotrajna</li>
                      <li>Viša cijena</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Silikatna fasada</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Dobra paropropusnost</li>
                      <li>Otporna na alge i plijesan</li>
                      <li>Srednja cijena</li>
                      <li>Manje elastična</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Mineralna fasada</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Najniža cijena</li>
                      <li>Odlična paropropusnost</li>
                      <li>Potrebno bojanje nakon postavljanja</li>
                      <li>Manja otpornost na vremenske uvjete</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite visinu i širinu zida</li>
                  <li>Unesite površinu prozora i vrata (ako postoje)</li>
                  <li>Odaberite vrstu i debljinu izolacije</li>
                  <li>Odaberite vrstu završnog sloja</li>
                  <li>Označite dodatne opcije prema potrebi</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračuni su informativne prirode</li>
                  <li>Stvarni troškovi mogu varirati ovisno o:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Cijenama materijala u vašem području</li>
                      <li>- Složenosti projekta</li>
                      <li>- Pristupačnosti zidova</li>
                      <li>- Stanju postojećih zidova</li>
                    </ul>
                  </li>
                  <li>Preporučujemo konzultaciju s profesionalcima prije početka radova</li>
                  <li>Uvijek dodajte 5-10% na izračunatu količinu materijala za rastur</li>
                  <li>Provjerite lokalne propise o energetskoj učinkovitosti</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Fasaderske Radove</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Radove izvodite pri temperaturi između 5°C i 30°C</li>
                  <li>Izbjegavajte postavljanje fasade tijekom jakog sunca, kiše ili vjetra</li>
                  <li>Pravilno pripremite podlogu - mora biti čista, suha i čvrsta</li>
                  <li>Koristite kutne profile za precizne i ravne rubove</li>
                  <li>Osigurajte pravilno preklapanje armaturne mrežice (min. 10 cm)</li>
                  <li>Poštujte vrijeme sušenja između slojeva</li>
                  <li>Zaštitite prozore i vrata prije početka radova</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorFasade;